import { useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button, MoneyAccentField } from '@scramble/ui';
import { useGetMe } from '@/hooks/useGetMe';
import Analytics from '@/analyticsGA';
import { PAYMENT_METHODS } from '@/helpers/cash';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { BankRequisiteModal } from '@/modals/BankRequisiteModal';
import { ReviewTopUpModal } from '@/modals/ReviewTopUpModal';
import { AddCashDecisionWidget } from '../AddCashDecisionWidget';
import { AmountPresets } from '../AmountPresets';
import { ImportantNotes } from '../ImportantNotes';
import { PaymentMethod } from '../PaymentMethod';
import { VALIDATION_SCHEMA } from './schema';
import s from './index.module.scss';

const DEFAULT_AMOUNT = 100;

export const TopUpForm = () => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const { canAddCash } = useGetMe();
  const { openSingleModal } = useModal();
  const { t } = useTranslation();

  const onSubmit = async (values) => {
    if (!values.method || !values.amount) return;
    Analytics.addCashGoToNextPayment();

    if (values.method.type === PAYMENT_METHODS.bank) {
      return openSingleModal(MODAL_IDS.bank_requisite_modal);
    }
    openSingleModal(MODAL_IDS.review_top_up_modal);
  };

  const { values, errors, setFieldValue, handleSubmit } = useFormik({
    validationSchema: VALIDATION_SCHEMA(t),
    initialValues: { amount: DEFAULT_AMOUNT },
    onSubmit,
  });

  const canTopUp = canAddCash && !Object.keys(errors).length;

  return (
    <div className={s.wrapper}>
      <form className={s.form} onSubmit={handleSubmit}>
        <MoneyAccentField
          isFloat
          label={t('funds:top_up_form.amount')}
          name="amount"
          value={values.amount}
          placeholder="0.00"
          onChange={amount => {
            setFieldValue('amount', amount);
            setHasInteracted(true);
          }}
          onClear={() => {
            setFieldValue('amount', '');
            setHasInteracted(true);
          }}
        />

        <AmountPresets
          value={values.amount}
          className={s.presets}
          onClick={amount => {
            setFieldValue('amount', amount);
            setHasInteracted(true);
          }}
        />

        <PaymentMethod
          amount={values.amount}
          method={values.method}
          className={s.methods}
          onChange={method => setFieldValue('method', method)}
        />

        {values.method && (
          <Button
            type="submit"
            label={
              values.method.type === PAYMENT_METHODS.bank
                ? t('funds:top_up_form.continue')
                : t('funds:top_up_form.review')
            }
            theme="neutral"
            appearance="filled"
            size="lg"
            className={s.btn_submit}
            disabled={!canTopUp}
          />
        )}
      </form>
      <ImportantNotes />

      {values.amount && values.method && (
        <>
          <BankRequisiteModal amount={values.amount} />
          <ReviewTopUpModal amount={values.amount} method={values.method} />
        </>
      )}

      <AddCashDecisionWidget
        amount={values.amount}
        hasInteracted={hasInteracted}
      />
    </div>
  );
};
